import React from "react";

import { ArrowRightOutlined } from "@ant-design/icons";
import { Button } from "antd";

import StyledScrollbar from "../../../../../Common/UIComponents/StyledScrollbar";
import {
  isCustomSpecProduct,
  getTestTypeDisplayLabel,
  formatSpecValueFromEditorShape,
  formatMrlCountriesListSubtitleFromEditorShape,
} from "../Helper";
import ProductSpecTypeBadge from "../ProductSpecTypeBadge";
import CoverageSummarySection from "./CoverageSummarySection";
import TestSpecStatusChip from "./TestSpecStatusChip";

import { getProductDisplayData } from "../../../utils";

import backIcon from "../../../../../../assets/images/product/backIconDarkBlue.png";
import { ReactComponent as MRLIcon } from "../../../../../../assets/images/product/MRLIcon.svg";
import { ReactComponent as MicroScopeIcon } from "../../../../../../assets/images/product/specmanager/microscope-line.svg";
import { ReactComponent as StackFillIcon } from "../../../../../../assets/images/product/specmanager/stack-fill.svg";

import "./TestSpecOverview.css";
import "./GlobalSpecTable.css";

const TestSpecOverview = ({
  selectedTestName,
  testStandardSpec,
  products = [],
  fields = [],
  linkReportJsonFields = [],
  testSpecStatus = "pending", // "added" | "pending"
  /**
   * When false, overview is read-only: no role permission and/or parent could not load spec detail
   * (e.g. API error). Hides the Edit Spec control and uses the non-editor empty-state copy.
   */
  canEditTestSpec = true,
  onBack,
  onEdit,
}) => {
  const customProducts = products.filter(isCustomSpecProduct).length;
  const totalProducts = products.length;
  const standardProducts = Math.max(totalProducts - customProducts, 0);

  const isPending = testSpecStatus === "pending";

  const countriesData = Array.isArray(testStandardSpec?.countries_lst)
    ? testStandardSpec.countries_lst
    : [];
  /** Match Test Spec list: MRL markets come from `countries_lst` / API `countries_list` for all viewers, not permission-gated. */
  const isMRL = countriesData.length > 0;

  const testTypeValue = isMRL ? (
    <div className="TestSpecOverview__TestTypeValue">
      <MRLIcon />
      <span>MRL</span>
    </div>
  ) : (getTestTypeDisplayLabel(testStandardSpec?.test_type) || "Not defined");
  const specValueLabel = isMRL ? "Not defined" : formatSpecValueFromEditorShape(testStandardSpec);
  const mrlListSubtitle = isMRL
    ? formatMrlCountriesListSubtitleFromEditorShape(countriesData, { includeMrlPrefix: false })
    : "";

  return (
    <div className="TestSpecOverview__Root">
      <div className="TestSpecOverview__Header">
        <div className="TestSpecOverview__HeaderRow">
          <div className="TestSpecOverview__HeaderLeft">
            <Button
              htmlType="button"
              className="TestSpecOverview__BackButton"
              onClick={onBack}
              aria-label="Back to test list"
            >
              <img src={backIcon} alt="Back" className="TestSpecOverview__BackIcon" />
            </Button>
            <div className="TestSpecOverview__TestName" title={selectedTestName}>{selectedTestName}</div>
          </div>
          <div className="TestSpecOverview__HeaderRight">
            <div className="GlobalSpecTable__ProductCountBadge">
              <StackFillIcon className="GlobalSpecTable__ProductCountBadgeIcon" />
              {totalProducts}
            </div>
            <TestSpecStatusChip status={isPending ? "pending" : "added"} />
          </div>
        </div>
      </div>

      <div className="TestSpecOverview__DetailsArea">
        <div className="TestSpecOverview__DetailsBox">
          <div className="TestSpecOverview__DetailsColumns">
            <div className="TestSpecOverview__StandardSection">
              <div className="TestSpecOverview__SectionTitleBar">
                <div className="TestSpecOverview__SectionTitle">Test Standard</div>
              </div>
              <div className="TestSpecOverview__StandardRows">
                <div className="TestSpecOverview__StandardRow">
                  <div className="TestSpecOverview__RowLabel">Test Type</div>
                  <div className={`TestSpecOverview__RowValue ${testTypeValue === "Not defined" ? "TestSpecOverview__RowValue--notDefined" : ""}`}>
                    {testTypeValue}
                  </div>
                </div>
                <div className="TestSpecOverview__StandardRow">
                  <div className="TestSpecOverview__RowLabel">Spec</div>
                  {isMRL ? (
                    <div
                      className={`TestSpecOverview__RowValue ${mrlListSubtitle === "MRL" ? "TestSpecOverview__RowValue--notDefined" : ""}`}
                      title={mrlListSubtitle}
                    >
                      {mrlListSubtitle}
                    </div>
                  ) : (
                    <div className={`TestSpecOverview__RowValue ${specValueLabel === "Not defined" ? "TestSpecOverview__RowValue--notDefined" : ""}`}>
                      {specValueLabel}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <CoverageSummarySection products={products} />
          </div>
        </div>
      </div>

      <div className="TestSpecOverview__ProductsArea">
        <div className="TestSpecOverview__AttachedProductsTitle">Attached Products</div>

        {products.length === 0 ? (
          <div className="TestSpecOverview__EmptyState">
            {canEditTestSpec
              ? "No products are currently using this standard. Click edit to add products."
              : "No products are currently using this standard."}
          </div>
        ) : (
          <StyledScrollbar
            id="TestSpecOverview__AttachedListScrollBar"
            className="TestSpecOverview__AttachedListScrollbar"
          >
            <ul className="TestSpecOverview__AttachedList">
              {products.map((product) => {
                const isCustom = isCustomSpecProduct(product);
                const { titleToValue } = getProductDisplayData(product, fields, linkReportJsonFields);
                const entries = Object.entries(titleToValue || {});
                const lineText = entries.length > 0
                  ? entries.map(([k, v]) => `${k}: ${v != null ? v : ""}`).join(" \u2022 ")
                  : product.product_name || "";

                return (
                  <li key={product.product_id} className="TestSpecOverview__AttachedProductItem">
                    <span className="TestSpecOverview__AttachedProductText">
                      {lineText}
                    </span>
                    <ProductSpecTypeBadge
                      variant={isCustom ? "custom" : "standard"}
                      customSpecs={isCustom ? product.custom_specs : undefined}
                      appearance="testSpecOverview"
                    />
                  </li>
                );
              })}
            </ul>
          </StyledScrollbar>
        )}
      </div>

      <div className="TestSpecOverview__Footer">
        <div className="TestSpecOverview__FooterFollowsBadge">
          <MicroScopeIcon className="TestSpecOverview__FooterBadgeIcon" />
          <strong>
            {standardProducts}
            {" "}
            Products
          </strong>
          follow the standard spec
        </div>
        {canEditTestSpec ? (
          <Button
            type="primary"
            onClick={onEdit}
            icon={<ArrowRightOutlined />}
          >
            Edit Spec
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default TestSpecOverview;
